/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { DEFAULT_LANGUAGE, useAppConfig, useLocale } from '@airasia-rn/ui';
import { useQuery } from '@tanstack/react-query';
import { StationsApiService } from '../services/stationsApiService';
import { Station } from '../models';
import axios from 'axios';
import { StationTypeFilter } from '../models';

export const stationsKeys = {
  stations: (locale: string) => ['stations', locale],
};

export function useStationsApiService(): StationsApiService {
  const appConfig = useAppConfig();
  return useMemo(() => {
    return new StationsApiService(appConfig.config.env);
  }, [appConfig.config]);
}

export const useFetchStations = () => {
  const api = useStationsApiService();
  const { currentLanguage } = useLocale();
  const locale = currentLanguage || DEFAULT_LANGUAGE;

  return useQuery({
    queryKey: stationsKeys.stations(locale),
    queryFn: () => {
      try {
        return api.fetchStations(DEFAULT_LANGUAGE);
      } catch (e) {
        if (axios.isAxiosError(e) && e.status === 404) {
          return api.fetchStations();
        } else {
          throw e;
        }
      }
    },
    staleTime: 12 * 60 * 60 * 1000, // 12 hours, as stations list is static
  });
};

export const useStationsMap = (
  stationType: StationTypeFilter = StationTypeFilter.ALL,
) => {
  const { data: stations, isLoading, isError } = useFetchStations();
  const stationsMap = useMemo(() => {
    if (!stations) return {};
    return stations.reduce<Record<string, Station>>((map, station) => {
      const code = station.StationCode?.trim().toUpperCase();
      if (!code) return map;

      // filter only when stationType != ALL
      if (
        stationType !== StationTypeFilter.ALL &&
        station.StationType !== stationType
      ) {
        return map;
      }

      map[code] = station;
      return map;
    }, {});
  }, [stations]);

  return {
    stationsMap,
    isStationsLoading: isLoading,
    isStationsError: isError,
  };
};
